import {
  Autocomplete,
  Button,
  InputAdornment,
  TextField,
  autocompleteClasses,
} from "@mui/material";
import PropTypes from "prop-types";
import Iconify from "../iconify/Iconify";
import React, { useCallback, useEffect, useState } from "react";
import { colors } from "../../utility/types/constant";

interface Circle {
  _id?: string;
  circleName?: string;
  circleImage?: string;
}

interface CircleSearchProps {
  circles: Circle[];
  selectedCircle: Circle | null;
  // onSearchInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchInputChange: (inputValue: string) => void;
  onSearchSelect: (circle: Circle | null) => void;
  onReset: () => void;
}

const CircleSearch = (props: CircleSearchProps) => {
  // const dispatch = useDispatch<AppDispatch>();

  const {
    circles,
    selectedCircle,
    onSearchInputChange,
    onSearchSelect,
    onReset,
  } = props;

  const [filteredCircle, setFilteredCircle] = useState<Circle[]>([]);

  // debounce method to delay search until user pauses typing.
  // help in preventing unneccessary trigger onChange event.

  const debounce = (func: Function, delay: number) => {
    let timerId: NodeJS.Timeout;

    return (...args: any) => {
      if (timerId) {
        clearTimeout(timerId);
      }
      timerId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  useEffect(() => {
    setFilteredCircle(circles); // Initialize with all circles
  }, [circles]); // Update when circles prop changes

  // using useCallback help to help handle user search input.
  // prevent un-wanted re-renders.
  const debounceSearch = useCallback(
    (inputValues: string) => {
      const newFilteredCircle = circles.filter((c) =>
        c.circleName?.toLowerCase().includes(inputValues)
      );
      setFilteredCircle(newFilteredCircle);
    },
    [circles, setFilteredCircle]
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value.toLowerCase();
      onSearchInputChange(inputValue); // Notify parent component about the search input change
      debounce(debounceSearch, 300);
    },
    [debounceSearch, onSearchInputChange]
  );

  return (
    <>
      <Autocomplete
        sx={{ width: 480 }}
        autoHighlight
        popupIcon={null}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              [`& .${autocompleteClasses.option}`]: {
                typography: "body2",
              },
            },
          },
        }}
        options={filteredCircle}
        value={selectedCircle}
        onChange={(_, newValue) => onSearchSelect(newValue)}
        getOptionLabel={(circle) => circle.circleName ?? ""}
        isOptionEqualToValue={(option: Circle, value: Circle | null) =>
          option._id === (value ? value._id : null)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search circle..."
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify
                    icon="eva:search-fill"
                    sx={{
                      ml: 1,
                      width: 20,
                      height: 20,
                      color: "text.disabled",
                    }}
                  />
                </InputAdornment>
              ),
            }}
            // value={searchInput}
            onChange={handleInputChange}
          />
        )}
      />
      {selectedCircle ? (
        <Button
          size="small"
          variant="contained"
          sx={{
            backgroundColor: colors.blueRibbon,
            "&hover": {
              backgroundColor: colors.blueRibbon,
            },
          }}
          onClick={onReset}
        >
          Reset
        </Button>
      ) : null}
    </>
  );
};

export default CircleSearch;

CircleSearch.propTypes = {
  circles: PropTypes.array,
  selectedCircle: PropTypes.any,
  onSearchInputChange: PropTypes.func,
  onSearchSelect: PropTypes.func,
  onReset: PropTypes.func,
};
